import './IndividualStyle.scss';

import { type FC, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { FontLicenseType } from 'client/graphql/types/operations';

import useCart from './utils/useCart';
import formatCurrency from 'utils/formatCurrency';

import * as cellSizing from './utils/cellSizing';

interface Props {
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
  family: {
    id: number;
    completePrice: number;
    subPackageFontFacePrice: number;
    faces: { id: number }[];
  };
  face: {
    id: number;
    size: { slug: string; name: string; position: number };
    width: { slug: string; name: string; position: number };
    weight: { slug: string; name: string; position: number };
    description: string;
    singleCharacterPreview: { url: string | null };
    family: { fontFacePrice: number; discountedFontFacePrice: number };
  };
  packageHovered: boolean;
  disabled?: boolean;
  doubleHeight: boolean;
  onHover(identifier: number | null): void;
}

const IndividualStyle: FC<Props> = (props) => {
  const {
    licenseOption,
    family,
    face,
    disabled,
    packageHovered,
    doubleHeight,
    onHover,
  } = props;

  const { t } = useTranslation();

  const faces = useMemo(() => [face], [face]);

  const {
    loading,
    allFacesInOrder,
    complementaryFaceSelected,
    totalPrice,
    toggleFaces,
  } = useCart({
    licenseOption,
    family,
    faces,
  });

  return (
    <div
      onClick={toggleFaces}
      onMouseEnter={() => onHover(face.id)}
      onMouseLeave={() => onHover(null)}
      className={classNames('IndividualStyle', {
        'is-selected': allFacesInOrder,
        'is-updating': loading,
        'is-parentPackageHover': packageHovered,
        'is-pairInCart': complementaryFaceSelected,
        'is-disabled': disabled || licenseOption == null,
      })}
      data-test-id="add-style-to-cart"
      style={{
        width: `calc(${cellSizing.cellSize} + 1px)`,
        height: `calc(${
          doubleHeight ? cellSizing.doubleCellSize : cellSizing.cellSize
        } + 1px)`,
      }}
    >
      <div
        className={classNames('IndividualStyle-sample', {
          'is-doubleHeight': doubleHeight,
        })}
        style={
          face.singleCharacterPreview?.url
            ? {
                backgroundImage: `url('${face.singleCharacterPreview.url}')`,
              }
            : undefined
        }
      />
      <div className="IndividualStyle-price">
        {disabled || licenseOption == null ? (
          <>{t('family.unavailable')} </>
        ) : (
          formatCurrency(totalPrice)
        )}
      </div>
      <div className="IndividualStyle-removeIcon">✕</div>
      <div className="IndividualStyle-description">
        {face.description.replace(/^Standard /, '')}
      </div>
    </div>
  );
};

export default IndividualStyle;
